import {
  Alert,
  Box,
  Button,
  DialogContentText,
  Typography,
} from '@mui/material';
import {
  deDE,
  GridColDef,
  GridRowParams,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import React from 'react';
import { useAlert } from '../../../context/AlertContext';
import { useConfirmation } from '../../../context/ConfirmationProvider';
import {
  useAnswerInvite,
  useGetInvitesBy,
} from '../../../hooks/queries/InviteQueries';
import { IInvite } from '../../../Interfaces/IInvites';
import { LoadingDialog } from '../../core/LoadingDialog';
import { LoadingSpinner } from '../../core/LoadingSpinner';
import { StyledDataGrid } from '../../core/StyledDataGrid';

const InvitationsMain: React.FC = () => {
  const { showConfirmation } = useConfirmation();

  const { showAlert } = useAlert();
  const contactId = localStorage.getItem('idContact');
  const { invites, isLoadingInvites, errorInvites, refetchInvites } =
    useGetInvitesBy(Number(contactId));

  const { onAnswerInvite, isPendingAnswerInvite } = useAnswerInvite();

  const handleOpenConfirmDialog = async (invite: IInvite) => {
    if (invite.isAssumed) return;

    const status = await showConfirmation({
      title: 'Einladung bestätigen/ablehnen',
      message: (
        <>
          <DialogContentText id='confirm-dialog-description' sx={{ mb: 2 }}>
            Bitte überprüfe die folgenden Details und bestätigen/ablehnen diese
            Einladung:
          </DialogContentText>
          <Typography variant='body2' sx={{ mb: 1 }}>
            <strong>E-Mail:</strong> {invite.Email}
          </Typography>
          <Typography variant='body2' sx={{ fontStyle: 'italic' }}>
            {invite.Text}
          </Typography>
        </>
      ),
      confirmButtonText: 'Annehmen',
      rejectButtonText: 'Ablehnen',
    });

    if (status === 'aborted') return;

    try {
      await onAnswerInvite({
        ...invite,
        isAssumed: status === 'confirmed' ? true : false,
      });

      showAlert({ text: 'Einladung erfolgreich beantwortet' });
      await refetchInvites();
    } catch (error) {
      await refetchInvites();

      showAlert({
        text: 'Fehler beim Beantworten der Einladung',
        severity: 'error',
      });
    }
  };

  if (isPendingAnswerInvite) return <LoadingDialog />;

  if (isLoadingInvites) return <LoadingSpinner />;

  if (errorInvites) {
    return (
      <Box sx={{ padding: 2 }}>
        <Alert
          severity='error'
          action={
            <Button
              color='inherit'
              size='small'
              onClick={() => refetchInvites()}
            >
              Neu laden
            </Button>
          }
        >
          Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <Typography variant='h5' gutterBottom>
        Meine Einladungen
      </Typography>

      <StyledDataGrid
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        autoHeight
        localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
        rows={invites!}
        columns={columns}
        getRowId={row => row.idContactInvite}
        onRowClick={(params: GridRowParams<IInvite>) =>
          handleOpenConfirmDialog(params.row)
        }
      />
    </Box>
  );
};

const columns: GridColDef[] = [
  { field: 'Email', headerName: 'E-Mail', width: 250 },
  { field: 'Text', headerName: 'Details', flex: 1, minWidth: 300 },
  {
    field: 'Timestamp',
    headerName: 'Erstellt',
    width: 180,
    valueGetter: (params: GridValueGetterParams<IInvite>) =>
      params.value ? new Date(params.value).toLocaleString() : '',
  },
];

export default InvitationsMain;
